import { hasStackedLayers, isOpenLayer, isNuovaDocument, setStackListener, topLayer } from './nv-layer-stack.js';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      lib util — «UI nuova» phone Back button (MOBILE-NATIVE-PLAN W1.1): the
 *              browser / Android Back (and the iOS edge swipe) closes the TOPMOST open
 *              overlay instead of leaving the page under it. While the open-order stack
 *              (lib/nv-layer-stack) is not empty, ONE sentinel history entry (same URL)
 *              sits on top of the page's entry: Back pops it, the popstate is swallowed
 *              before Ember's router sees it, and the top layer runs its own close path.
 *   extends:   -
 *   template:  none
 *   args:      -
 *   uses:      lib/nv-layer-stack (topLayer, hasStackedLayers, setStackListener,
 *              isNuovaDocument), window.history, the router service (routeWillChange)
 *   siblings:  services/ui.js (installs on apply() when the nuova is on, uninstalls otherwise)
 *   tests:     medical tests/unit/lib/nv-layer-history (unit)
 *   gotchas:   (1) The popstate listener is CAPTURE on window: at-target, capture
 *              listeners run before Ember's HistoryLocation listener, so
 *              stopImmediatePropagation keeps a consumed Back away from the router.
 *              (2) A layer that refuses to close (busy save, dirty-form alert) stays
 *              on the stack: the next sync pushes a new sentinel, so the following
 *              Back is still owned by the overlay. (3) Closing by ✕ / Esc / backdrop
 *              consumes the sentinel with our own history.back() (counted in
 *              `selfPops`, swallowed). (4) A route change while the sentinel is up
 *              (a link inside a sheet) REPLACES the sentinel entry
 *              (transition.method('replace')), else a phantom same-URL entry would
 *              need an extra Back; routeDidChange re-syncs, so a layer that
 *              survives the change (query params) gets a fresh sentinel. (5) The stack can empty and refill within one
 *              action (sheet item opens a modal): sync is deferred one task.
 *              (6) history.scrollRestoration = 'manual' while installed: the browser
 *              restored 0 (the scroll saved under the locked body) after our own Back.
 *              (7) Classic dropdown panes (`.dropdown-pane.is-open`, toggled by the
 *              shell body-click handler, a bottom sheet on phones) and
 *              `[data-nv-layer]` overlays (rail, search, calendar popup, combobox)
 *              are not on the Escape stack: a MutationObserver tracks them here,
 *              and Back closes the last opened one (pane: class off; data-nv-layer:
 *              Escape) before the stack's top. A [data-nv-layer] mounted with its
 *              element (combobox list) is caught by the childList records. (8) One of
 *              those that ignores Escape twice stops owning Back (stuckPanes): Back
 *              is never trapped on a page — unless it is marked data-nv-back="hold"
 *              (it refuses on purpose and shows why, e.g. calendar popup with typed data).
 */
const SENTINEL_KEY = '__nvLayer';
let router = null;
let installed = false;
// location.href when our sentinel entry was pushed; null = no sentinel on top of history
let sentinelUrl = null;
// history.back() calls of ours whose popstate must be swallowed
let selfPops = 0;
let syncTimer = null;
let previousScrollRestoration = null;

// Open overlays that are not on the Escape stack, tracked HERE in open order (Escape semantics stay untouched):
// dropdown panes (`.dropdown-pane.is-open`, opened by the shell's body-click handler, a bottom sheet on phones)
// and `[data-nv-layer]` overlays (phone rail, top-bar search, calendar popup, combobox lists, colour picker),
// which all close on Escape.
const PANE_SELECTOR = '.dropdown-pane.is-open, [data-nv-layer]';
const openPanes = [];
let paneObserver = null;
// Escape-closed overlays that did not close on Back: after STUCK_AFTER tries they stop owning Back, so Back can
// never be trapped on a page. Stack entries (explicit close paths, a busy save that must hold Back) never get here.
const STUCK_AFTER = 2;
let paneAttempts = new WeakMap();
let stuckPanes = new WeakSet();
function closePane(pane) {
  if (!pane.classList.contains('dropdown-pane')) {
    pane.ownerDocument.dispatchEvent(new KeyboardEvent('keydown', {
      key: 'Escape',
      bubbles: true
    }));
    return;
  }
  pane.classList.remove('is-open');
  if (!document.querySelector('.dropdown-pane.is-open')) {
    document.body?.classList.remove('dropdown-open');
  }
}
function reconcilePanes() {
  let changed = false;
  for (let i = openPanes.length - 1; i >= 0; i -= 1) {
    const pane = openPanes[i];
    if (!pane.isConnected || !pane.matches(PANE_SELECTOR)) {
      // it closed: the next time it opens it owns Back again
      openPanes.splice(i, 1);
      paneAttempts.delete(pane);
      stuckPanes.delete(pane);
      changed = true;
    }
  }
  for (const pane of document.querySelectorAll(PANE_SELECTOR)) {
    if (!openPanes.includes(pane) && isOpenLayer(pane)) {
      openPanes.push(pane);
      changed = true;
    }
  }
  if (changed) scheduleSync();
}
function addsLayer(node) {
  if (node.nodeType !== 1) return false;
  return node.matches(PANE_SELECTOR) || Boolean(node.querySelector('[data-nv-layer]'));
}
function isPaneRecord(record) {
  if (record.type === 'attributes') {
    return record.attributeName === 'data-nv-layer' || record.target?.classList?.contains('dropdown-pane');
  }
  // childList: an overlay mounted with its attribute (combobox list, colour picker), or a tracked one removed
  if (openPanes.length > 0 && record.removedNodes.length > 0) return true;
  return [...record.addedNodes].some(addsLayer);
}
function onPaneMutations(records) {
  if (records.some(isPaneRecord)) reconcilePanes();
}
function hasLayers() {
  return hasStackedLayers() || openPanes.some(pane => !stuckPanes.has(pane));
}

/** Closes the topmost overlay: an open pane first (it sits over whatever opened it), else the stack's top. */
function closeTop() {
  const pane = [...openPanes].reverse().find(p => !stuckPanes.has(p));
  if (pane) {
    const attempts = (paneAttempts.get(pane) ?? 0) + 1;
    paneAttempts.set(pane, attempts);
    // data-nv-back="hold": the overlay refuses on purpose and tells why (calendar popup with typed data) — never
    // let Back past it, the page change would discard what was typed
    if (attempts > STUCK_AFTER && pane.dataset?.nvBack !== 'hold') {
      stuckPanes.add(pane);
      return;
    }
    closePane(pane);
    return;
  }
  const top = topLayer();
  if (top?.close) {
    top.close();
  } else {
    // a layer registered without a close path: every overlay closes on Escape
    document.dispatchEvent(new KeyboardEvent('keydown', {
      key: 'Escape',
      bubbles: true
    }));
  }
}
function pushSentinel() {
  const state = {
    ...(window.history.state ?? {}),
    [SENTINEL_KEY]: true
  };
  window.history.pushState(state, '', window.location.href);
  sentinelUrl = window.location.href;
}
function sync() {
  syncTimer = null;
  if (!installed || !isNuovaDocument()) return;
  if (hasLayers()) {
    // our own Back is in flight: its popstate re-syncs and pushes the new sentinel then
    if (sentinelUrl === null && selfPops === 0) pushSentinel();
    return;
  }
  if (sentinelUrl === null) return;
  const stillOnSentinel = window.location.href === sentinelUrl && Boolean(window.history.state?.[SENTINEL_KEY]);
  sentinelUrl = null;
  if (stillOnSentinel) {
    selfPops += 1;
    window.history.back();
  }
}
function scheduleSync() {
  if (syncTimer !== null) return;
  syncTimer = setTimeout(sync, 0);
}
function onPopState(event) {
  if (selfPops > 0) {
    selfPops -= 1;
    event.stopImmediatePropagation();
    scheduleSync();
    return;
  }
  // a Back with no overlay open belongs to the router
  if (sentinelUrl === null || !isNuovaDocument()) return;
  sentinelUrl = null;
  event.stopImmediatePropagation();
  closeTop();
  scheduleSync();
}
function onRouteWillChange(transition) {
  if (sentinelUrl === null || !transition || transition.isAborted) return;
  // the new URL takes the sentinel's place: no phantom same-URL entry behind the next page
  transition.method?.('replace');
  sentinelUrl = null;
}

// a layer still open after the route changed (a query-param update under a sheet) needs a new sentinel
function onRouteDidChange() {
  reconcilePanes();
  scheduleSync();
}

/** Installs the Back handling (idempotent). `routerService`: the router service of the app. */
function installLayerHistory(routerService) {
  if (installed || typeof window === 'undefined' || !window.history?.pushState) return;
  installed = true;
  router = routerService ?? null;
  // our own history.back() must not let the browser restore the entry's scroll (saved while the page was
  // locked under the sheet = 0); the shell owns scrolling on route changes anyway
  previousScrollRestoration = window.history.scrollRestoration ?? null;
  if (previousScrollRestoration !== null) window.history.scrollRestoration = 'manual';
  window.addEventListener('popstate', onPopState, true);
  router?.on?.('routeWillChange', onRouteWillChange);
  router?.on?.('routeDidChange', onRouteDidChange);
  setStackListener(scheduleSync);
  if (typeof MutationObserver === 'function' && document.body) {
    paneObserver = new MutationObserver(onPaneMutations);
    paneObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class', 'data-nv-layer'],
      childList: true,
      subtree: true
    });
  }
  reconcilePanes();
  if (hasLayers()) scheduleSync();
}

/** Removes the Back handling; a sentinel still on top stays (one harmless same-URL entry). */
function uninstallLayerHistory() {
  if (!installed) return;
  installed = false;
  window.removeEventListener('popstate', onPopState, true);
  if (previousScrollRestoration !== null) window.history.scrollRestoration = previousScrollRestoration;
  previousScrollRestoration = null;
  router?.off?.('routeWillChange', onRouteWillChange);
  router?.off?.('routeDidChange', onRouteDidChange);
  router = null;
  setStackListener(null);
  paneObserver?.disconnect();
  paneObserver = null;
  openPanes.length = 0;
  paneAttempts = new WeakMap();
  stuckPanes = new WeakSet();
  if (syncTimer !== null) clearTimeout(syncTimer);
  syncTimer = null;
  sentinelUrl = null;
  selfPops = 0;
}

/** Test seam: the internal state. */
function layerHistoryState() {
  return {
    installed,
    sentinelUrl,
    selfPops,
    openPanes: openPanes.length,
    stacked: hasStackedLayers(),
    hasLayers: hasLayers()
  };
}

export { installLayerHistory, layerHistoryState, uninstallLayerHistory };
//# sourceMappingURL=nv-layer-history.js.map
